import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { PageMeta } from "@/components/site/PageMeta";
import { PageHero } from "@/components/site/PageHero";
import { Reveal } from "@/components/site/Reveal";
import { CTABanner } from "@/components/site/CTABanner";
import { PhotoBackground } from "@/components/site/PhotoBackground";
import { CASE_ARCHETYPES, CATEGORIES, GLOSSARY, METHOD_STEPS, SERVICES } from "@/data/content";

const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

const HERO_IMG =
    "https://images.unsplash.com/photo-1701290614381-dca2da8a47cb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwxfHxkZXNlcnQlMjBhcmNoaXRlY3R1cmFsJTIwbW9kZXJuJTIwY29uY3JldGUlMjB0dWNzb24lMjBtb3VudGFpbnMlMjBtaW5pbWFsaXN0JTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc4ODcyMzgxNHww&ixlib=rb-4.1.0&q=85";
const GRID_IMG =
    "https://images.unsplash.com/photo-1774021792718-e73106917e52?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwyfHxtaW5pbWFsaXN0JTIwbW9kZXJuJTIwYXJjaGl0ZWN0dXJlJTIwc3RlZWwlMjBnbGFzcyUyMGZhY2FkZSUyMGRhcmslMjBpbnN0aXR1dGlvbmFsfGVufDB8fHx8MTc4ODcyMzgyMXww&ixlib=rb-4.1.0&q=85";
const METHOD_IMG =
    "https://images.unsplash.com/photo-1750056661722-8381f9012079?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaH3Y29uY3JldGUlMjB0ZXh0dXJlfGVufDB8fHx8MTc5MDk4ODc0N3ww&ixlib=rb-4.1.0&q=85";
const ARCHETYPES_IMG =
    "https://images.unsplash.com/photo-1579492450119-80542d516179?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaH3Y29uY3JldGUlMjB3YWxsfGVufDB8fHx8MTc5MDk4ODc0N3ww&ixlib=rb-4.1.0&q=85";

const Filters = ({ active, setActive }) => (
    <div role="tablist" aria-label="Filter services by domain" className="flex flex-wrap gap-3">
        {["All", ...CATEGORIES].map((cat) => {
            const isActive = active === cat;
            return (
                <button
                    key={cat}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    data-testid={`services-filter-${cat === "All" ? "all" : slugify(cat)}`}
                    onClick={() => setActive(cat)}
                    className={`border px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] transition-colors duration-300 ${
                        isActive
                            ? "border-slate-200 bg-slate-100 text-slate-900"
                            : "border-slate-800 text-slate-400 hover:border-slate-600 hover:text-slate-200"
                    }`}
                >
                    {cat}
                </button>
            );
        })}
    </div>
);

const ServiceCard = ({ service }) => (
    <motion.article
        layout
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -12 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        data-testid={`service-card-${service.num}`}
        className="group flex h-full flex-col border border-amber-500/70 bg-[#0b0f16] p-8 transition-all duration-300 hover:-translate-y-0.5 hover:border-amber-400 hover:bg-[#0e141d]"
    >
        <div className="flex items-baseline justify-between">
            <span className="font-mono text-sm tracking-[0.25em] text-amber-500 transition-colors duration-300 group-hover:text-amber-400">
                {service.num}
            </span>
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-600">
                {service.category}
            </span>
        </div>
        <h3 className="mt-8 font-serif text-2xl font-medium leading-snug tracking-tight text-slate-100">
            {service.title}
        </h3>
        <p className="mt-4 flex-1 text-sm leading-relaxed text-slate-400">{service.description}</p>
        <div className="mt-8 flex flex-wrap gap-2 border-t border-slate-800 pt-6">
            {service.outcomes.map((o) => (
                <span key={o} className="border border-slate-800 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.15em] text-slate-500">
                    {o}
                </span>
            ))}
        </div>
    </motion.article>
);

const Methodology = () => (
    <section className="relative overflow-hidden border-b border-slate-800/70">
        <PhotoBackground src={METHOD_IMG} />
        <div className="relative mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-12 lg:py-28">
            <Reveal>
                <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-blue-400">Methodology</p>
                <h2 className="mt-5 max-w-2xl font-serif text-3xl font-medium leading-tight tracking-tight text-slate-100 sm:text-4xl lg:text-5xl">
                    How an engagement runs.
                </h2>
            </Reveal>
            <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
                {METHOD_STEPS.map((step, i) => (
                    <Reveal key={step.num} delay={i * 0.08} className="h-full">
                        <div data-testid={`method-step-${step.num}`} className="group flex h-full flex-col border border-amber-500/70 bg-[#0b0f16] p-8 transition-colors duration-300 hover:border-amber-400 hover:bg-[#0e141d]">
                            <div className="flex items-center justify-between">
                                <span className="font-mono text-sm tracking-[0.25em] text-amber-500 transition-colors duration-300 group-hover:text-amber-400">{step.num}</span>
                                {i < METHOD_STEPS.length - 1 && <ArrowRight size={14} className="text-slate-700" />}
                            </div>
                            <h3 className="mt-10 font-serif text-2xl font-medium text-slate-100">{step.title}</h3>
                            <p className="mt-4 text-sm leading-relaxed text-slate-400">{step.body}</p>
                        </div>
                    </Reveal>
                ))}
            </div>
        </div>
    </section>
);

const Archetypes = () => (
    <section className="relative overflow-hidden border-b border-slate-800/70">
        <PhotoBackground src={ARCHETYPES_IMG} />
        <div className="relative mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-12 lg:py-28">
            <Reveal>
                <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-blue-400">Engagement Archetypes</p>
                <h2 className="mt-5 max-w-2xl font-serif text-3xl font-medium leading-tight tracking-tight text-slate-100 sm:text-4xl lg:text-5xl">
                    Composite examples. No client identities.
                </h2>
                <p className="mt-6 max-w-2xl text-sm leading-relaxed text-slate-500">
                    The following are anonymized composites drawn from common engagement patterns.
                    They describe types of work, not named clients, whose confidentiality is absolute.
                </p>
            </Reveal>
            <div className="mt-14 grid gap-6 lg:grid-cols-3">
                {CASE_ARCHETYPES.map((c, i) => (
                    <Reveal key={c.title} delay={i * 0.08} className="h-full">
                        <article data-testid={`archetype-card-${i + 1}`} className="group flex h-full flex-col border border-amber-500/70 bg-[#0b0f16] p-8 transition-colors duration-300 hover:border-amber-400 hover:bg-[#0e141d]">
                            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-amber-500">
                                {c.context}
                            </span>
                            <h3 className="mt-6 font-serif text-2xl font-medium text-slate-100">{c.title}</h3>
                            <p className="mt-4 flex-1 text-sm leading-relaxed text-slate-400">{c.body}</p>
                            <p className="mt-8 border-t border-slate-800 pt-6 text-xs leading-relaxed text-slate-500">
                                <span className="font-mono uppercase tracking-[0.2em] text-slate-400">Outcome: </span>
                                {c.result}
                            </p>
                        </article>
                    </Reveal>
                ))}
            </div>
        </div>
    </section>
);

const Glossary = () => (
    <section data-testid="glossary-section" className="border-b border-slate-800/70 bg-[#090d14]">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 py-20 sm:px-6 lg:grid-cols-12 lg:px-12 lg:py-28">
            <Reveal className="lg:col-span-4">
                <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-blue-400">Plain-English Compliance</p>
                <h2 className="mt-5 font-serif text-3xl font-medium leading-tight tracking-tight text-slate-100 sm:text-4xl">
                    The rules, translated.
                </h2>
                <p className="mt-6 text-sm leading-relaxed text-slate-400">
                    Four frameworks shape nearly every collection conversation. Here is what they
                    actually require, in plain English.
                </p>
                <p className="mt-6 border-l-2 border-blue-500/60 pl-5 text-xs leading-relaxed text-slate-500">
                    General information for orientation only, not legal advice.
                </p>
            </Reveal>
            <div className="space-y-4 lg:col-span-8">
                {GLOSSARY.map((g, i) => (
                    <Reveal key={g.index} delay={i * 0.05}>
                        <div data-testid={`glossary-row-${i + 1}`} className="grid gap-2 border border-amber-500/70 bg-[#0b0f16]/60 px-5 py-8 transition-colors duration-300 hover:border-amber-400 hover:bg-[#0e141d] sm:grid-cols-12 sm:gap-8 sm:px-6">
                            <span className="font-mono text-[11px] tracking-[0.25em] text-amber-500 sm:col-span-2">
                                {g.index}
                            </span>
                            <div className="sm:col-span-4">
                                <h3 className="font-serif text-xl text-slate-100 sm:text-2xl">{g.term}</h3>
                                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                                    {g.expansion}
                                </p>
                            </div>
                            <p className="text-sm leading-relaxed text-slate-400 sm:col-span-6">{g.body}</p>
                        </div>
                    </Reveal>
                ))}
            </div>
        </div>
    </section>
);

export default function Services() {
    const [active, setActive] = useState("All");
    const filtered = active === "All" ? SERVICES : SERVICES.filter((s) => s.category === active);

    return (
        <>
            <PageMeta
                title="Services | Nationwide Strategies LLC"
                description="Fourteen consulting specialties across executive advisory, compliance and risk, operations and performance, and people development for collection agencies and receivables organizations."
            />
            <PageHero
                testid="services-hero"
                kicker="The Portfolio"
                lines={["Consulting across the", "full weight of compliance."]}
                lede="Fourteen advisory specialties organized across four domains, each delivered principal-led, each measured against the outcomes leadership is accountable for."
                image={HERO_IMG}
            />
            <section className="relative overflow-hidden border-b border-slate-800/70">
                <PhotoBackground src={GRID_IMG} />
                <div className="relative mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-12 lg:py-20">
                    <Reveal>
                        <Filters active={active} setActive={setActive} />
                    </Reveal>
                    <motion.div layout className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                        <AnimatePresence mode="popLayout">
                            {filtered.map((s) => (
                                <ServiceCard key={s.num} service={s} />
                            ))}
                        </AnimatePresence>
                    </motion.div>
                </div>
            </section>
            <Methodology />
            <Archetypes />
            <Glossary />
            <CTABanner />
        </>
    );
}
